/**
 * HTML source mode helpers: decide whether the visual editor (TipTap schema) would change a piece of HTML.
 *
 * The comparison parses the HTML with the editor schema, serializes it again and compares both sides in a
 * normalized form (attribute order, style declaration order/spacing, hex vs rgb colors, whitespace between
 * elements), so only real losses (unknown tags, attributes, styles) count.
 */
import { getHTMLFromFragment } from '@tiptap/core';
import { DOMParser as ProseMirrorParser, type Schema } from '@tiptap/pm/model';

const HEX = /#([0-9a-f]{3}|[0-9a-f]{6})\b/gi;

function hexToRgb(hex: string): string {
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16));
  return `rgb(${r}, ${g}, ${b})`;
}

function normalizeStyle(style: string): string {
  return style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const index = declaration.indexOf(':');
      const value = declaration
        .slice(index + 1)
        .trim()
        .replace(/\s+/g, ' ')
        .replace(HEX, (_match, hex: string) => hexToRgb(hex))
        .replace(/\s*,\s*/g, ', ');
      return `${declaration.slice(0, index).trim().toLowerCase()}: ${value.toLowerCase()}`;
    })
    .sort()
    .join('; ');
}

function serialize(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? '').replace(/\s+/g, ' ');
    return text.trim() === '' ? '' : text;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const element = node as Element;
  const attributes = Array.from(element.attributes)
    .map((attribute) => [attribute.name.toLowerCase(), attribute.name.toLowerCase() === 'style' ? normalizeStyle(attribute.value) : attribute.value] as const)
    .filter(([, value]) => value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => ` ${name}="${value}"`)
    .join('');
  const tag = element.tagName.toLowerCase();
  return `<${tag}${attributes}>${Array.from(element.childNodes).map(serialize).join('')}</${tag}>`;
}

/** Comparable form of an HTML fragment. */
export function normalizeHtml(html: string): string {
  return Array.from(new DOMParser().parseFromString(html, 'text/html').body.childNodes).map(serialize).join('');
}

/** What the visual editor would keep of `html` (parse with the schema, serialize again). */
export function schemaRoundTrip(html: string, schema: Schema): string {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  return getHTMLFromFragment(ProseMirrorParser.fromSchema(schema).parse(body).content, schema);
}

/** True if switching to the visual editor would drop or change markup of `html`. */
export function isLossy(html: string, schema: Schema): boolean {
  return normalizeHtml(html) !== normalizeHtml(schemaRoundTrip(html, schema));
}

/** One block per line for the source view (the visual editor ignores the white space between blocks). */
export function prettyHtml(html: string): string {
  if (html.includes('\n')) return html;
  return html.replace(/(<(?:tbody|ul|ol|table|tr)[^>]*>|<\/(?:p|li|ul|ol|blockquote|h[1-6]|tr|td|th|table|tbody|div)>|<hr[^>]*>)/g, '$1\n').trim();
}

export type SanitizeChange = 'scripts' | 'other' | null;

/**
 * How the server changed saved HTML: `scripts` if scripts, event attributes or javascript: links were removed,
 * `other` if anything else differs (unsupported tags, styles, remote pictures), null if unchanged.
 */
export function sanitizeChange(sent: string, saved: string): SanitizeChange {
  const unsafe = /<script|<iframe|\son[a-z]+\s*=|javascript:/i;
  if (unsafe.test(sent) && !unsafe.test(saved)) return 'scripts';
  return normalizeHtml(sent) === normalizeHtml(saved) ? null : 'other';
}
